import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const reflections = [
  [
    "Actions Are Loud",
    "People don’t hear what we say, they see what we do. Words about love are often forgotten, but people remember how we treat them. Our patience, generosity and willingness to show up tell a story.\nLet that story look like the faith we profess."
  ],
  [
    "Talk Is Cheap",
    "Saying “I’m here for you” is easy.\nMaking time for it will cost you something.\nOffer what you can genuinely give.\nKeep your promises and be honest when you can’t."
  ],
  [
    "Failure Is Not Forever",
    "A mistake can have real consequences without becoming your whole identity.\nOwn it and repair what you can.\nAccept help and be thankful for grace.\nIt allows you room to grow.\nTake a different step next time."
  ],
  [
    "Clouds Blow Away",
    "When life feels dark, you don’t have to pretend the sun is shining. Let someone sit with you. Accept support, take the day in smaller pieces, and leave room for hope without demanding that pain hurry."
  ],
  [
    "Practice What You Preach",
    "Before we correct someone else, we can ask whether we live by the same standard. An honest apology and a changed habit can teach more than another speech about doing better."
  ],
  [
    "Humility Is Greatness",
    "A title cannot make us too important to listen, apologize or lend a hand. Humility gives us room to learn and makes room for other people. Whatever our gifts, we remain people who need grace."
  ],
  [
    "Love Is True Power",
    "Love has the strength to tell the truth without trying to crush someone. It protects, listens and stays responsible for its actions. Use whatever influence you have to help another person stand, not to keep them beneath you."
  ],
  [
    "Don’t Toot Your Own Horn",
    "Do something good without making yourself the center of the story. Share the credit. Celebrate someone else’s gift. We can appreciate our own abilities without needing every room to recognize them."
  ],
  [
    "The Least Is the Greatest",
    "Notice who gets overlooked: the person without a title, the quiet voice, the one who cannot offer anything in return. Make room, learn their name and listen. Their worth never depended on recognition from somebody important."
  ],
  [
    "Be Merciful",
    "When someone falls short, remember how much grace you have needed too. Correct what needs correcting without enjoying their shame. Mercy makes room for accountability, repair and the possibility that a person can become more than their worst moment."
  ],
  [
    "Kindness Ain’t Blindness",
    "You can have a good heart and still pay attention. Kindness does not require ignoring patterns, excusing harm or giving everyone access to your life. Speak with care, keep wise boundaries, and let compassion include you too."
  ],
  [
    "The Greatest Is the Least",
    "If people look to you for leadership, look for a way to serve them. Carry your share. Do the unnoticed work. Being trusted with responsibility should make us more available to people, not harder to reach."
  ],
  [
    "Seek the Truth",
    "Ask honest questions, including those that challenge your own position. Check what you repeat. Listen before deciding you already know. Seeking truth takes humility to say, “I was wrong,” and courage to change how we live."
  ],
  [
    "It’s Not Weak to Be Meek",
    "You can be firm without being cruel, and courageous without being loud. Meekness gives strength a purpose beyond winning every argument. Speak up when harm is happening, and choose a response that does not become another injury."
  ],
  [
    "Ministry Before Industry",
    "Before we ask what something earns or how far it reaches, we ask whom it serves. Music, money and opportunity should support the ministry. No audience, deal or applause is worth forgetting the person in front of us."
  ],
  [
    "Walk the Talk",
    "Choose one thing you say matters and give it a place in your day. Make the call. Offer the apology. Help with the work. Faith becomes visible when it moves from our intentions into somebody else’s experience."
  ],
  [
    "A Cross Doesn’t Make You a Christian",
    "A cross can be a meaningful expression of faith. Following Jesus also asks something of our lives: how we treat people, handle power and respond when we’re wrong. Let what we wear remind us of how we are called to live."
  ],
  [
    "Grace Doesn’t Keep Score",
    "Forgiveness can take time. We can release the wish to get even without calling the hurt acceptable or restoring trust too soon. Grace leaves room for healing, even when a relationship needs boundaries."
  ],
  [
    "Repair What You Can",
    "An honest apology makes room for the person we hurt. Listen without preparing your defense. Take responsibility, offer a practical repair, and give them time. Reconciliation grows through changed behavior, not pressure to move on."
  ],
  [
    "Leave Room to Learn",
    "Someone with a different story may notice what you have missed. Ask a real question and let the answer reach you. Growth begins when we stop treating every conversation as a chance to prove ourselves."
  ],
  [
    "Gratitude Changes the View",
    "Gratitude does not ask us to deny what is hard. It helps us notice what is still good: a shared meal, a faithful friend, another chance. Name the gift, thank the giver, and pass some goodness along."
  ],
  [
    "No Strings",
    "Give what you can responsibly share without making someone owe you attention, loyalty or praise. Let help remain help. Generosity protects the dignity of the person receiving it."
  ],
  [
    "No Audition Required",
    "You don’t have to impress us to be welcomed here. Bring your questions and your unfinished story. There is room for you before you sing a note, find the right words or prove what you can do."
  ],
  [
    "Rest Is Part of the Rhythm",
    "Even music needs space between the notes. We need room to breathe, recover and be people beyond what we produce. Rest helps us return with care to the work and the people we love."
  ]
];

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 230px), 1fr));
  gap: 12px 24px;
`;
const SloganButton = styled.button`
  padding: 10px 14px;
  min-width: 0;
  min-height: 44px;
  background: var(--about-surface);
  border: 1px solid #517f86;
  border-radius: 6px;
  color: ${p => p.theme.colors.white};
  font: 400 .95rem/1.5 ${p => p.theme.fonts.body};
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
  &:hover, &[aria-expanded="true"] {
    border-color: ${p => p.theme.colors.homeGold};
    background: #164854;
    color: ${p => p.theme.colors.homeGold};
  }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.homeGold}; outline-offset: 4px; }
`;
const Panel = styled.div`
  position: fixed;
  z-index: 1000;
  width: min(400px, calc(100vw - 24px));
  max-height: calc(100dvh - 24px);
  overflow-y: auto;
  padding: 20px;
  border: 1px solid ${p => p.theme.colors.accent};
  border-radius: 6px;
  background: #f3dfa9;
  color: ${p => p.theme.colors.homeNavy};
  box-shadow: 0 12px 32px #0006;
  font-family: ${p => p.theme.fonts.body};
  font-size: 1rem;
  line-height: 1.6;
  p {
    margin: 0;
    font-family: ${p => p.theme.fonts.body};
    font-size: 1.125rem;
    font-weight: 400;
    line-height: 1.4;
  }
`;

export default function MissionReflections() {
  const [open, setOpen] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [position, setPosition] = useState(null);
  const buttons = useRef([]);
  const panel = useRef(null);
  const closeTimer = useRef(null);
  const suppressMouseUntil = useRef(0);
  // Touch browsers can synthesize mouse events after a tap. Keep taps click-driven.
  const noteTouch = () => { suppressMouseUntil.current = Date.now() + 1000; };
  const mouseEnter = index => {
    if (Date.now() >= suppressMouseUntil.current) show(index);
  };
  const mouseLeave = () => {
    if (Date.now() >= suppressMouseUntil.current) delayedClose();
  };
  const cancelClose = () => clearTimeout(closeTimer.current);
  const show = index => {
    cancelClose();
    if (pinned !== null) return;
    if (open !== index) setPosition(null);
    setOpen(index);
  };
  const togglePinned = index => {
    cancelClose();
    if (pinned === index) {
      setPinned(null);
      setOpen(null);
    } else {
      if (open !== index) setPosition(null);
      setPinned(index);
      setOpen(index);
    }
  };
  const delayedClose = () => {
    cancelClose();
    if (pinned === null) closeTimer.current = setTimeout(() => setOpen(null), 250);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (open === null) return;
    const dismiss = event => {
      if (!panel.current?.contains(event.target) && !buttons.current.some(button => button?.contains(event.target))) {
        clearTimeout(closeTimer.current);
        setPinned(null);
        setOpen(null);
      }
    };
    const escape = event => {
      if (event.key === "Escape") {
        clearTimeout(closeTimer.current);
        setPinned(null);
        setOpen(null);
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("focusin", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("focusin", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (open === null) return;
    const place = () => {
      const anchor = buttons.current[open].getBoundingClientRect();
      const popup = panel.current.getBoundingClientRect();
      const inset = 12;
      const left = Math.max(inset, Math.min(anchor.left + (anchor.width - popup.width) / 2, window.innerWidth - popup.width - inset));
      const above = anchor.top - popup.height - 8;
      const preferredTop = above >= inset ? above : anchor.bottom + 8;
      const top = Math.max(inset, Math.min(preferredTop, window.innerHeight - popup.height - inset));
      setPosition({ left, top });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const observer = new ResizeObserver(place);
    observer.observe(panel.current);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  return <>
    <Grid>
      {reflections.map(([name], index) => <SloganButton
        key={name}
        type="button"
        ref={element => { buttons.current[index] = element; }}
        aria-expanded={open === index}
        aria-pressed={pinned === index}
        aria-controls={open === index ? "mission-reflection-panel" : undefined}
        aria-describedby={open === index ? "mission-reflection-panel" : undefined}
        onMouseEnter={() => mouseEnter(index)}
        onMouseLeave={mouseLeave}
        onTouchStart={noteTouch}
        onFocus={() => show(index)}
        onClick={() => togglePinned(index)}
      >{name}</SloganButton>)}
    </Grid>
    {open !== null && createPortal(<Panel
      ref={panel}
      id="mission-reflection-panel"
      role="tooltip"
      style={{ left: position?.left ?? 12, top: position?.top ?? 12, visibility: position ? "visible" : "hidden" }}
      onMouseEnter={cancelClose}
      onMouseLeave={mouseLeave}
    >
      <p>{reflections[open][1].split("\n").map((line, index) => (
        <React.Fragment key={index}>{index > 0 && <br />}{line}</React.Fragment>
      ))}</p>
    </Panel>, document.body)}
  </>;
}
